import {
  User,
  Calendar,
  ShoppingCart,
  Hash,
  Globe,
  UserCircle,
  Building,
  Fingerprint,
  ExternalLink,
} from 'lucide-react';
import { useShopifyAdmin } from '@/hooks/useShopifyAdmin';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { JourneyTimeline } from '@/components/journey';
import { formatDateTime } from '@/lib/utils';
import type { Journey } from '../types';

interface SessionDetailSheetProps {
  journey: Journey | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function InfoRow({
  icon: Icon,
  label,
  value,
  mono = false,
}: {
  icon: React.ElementType;
  label: string;
  value: string | React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
        <Icon className="h-4 w-4 text-slate-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-500 mb-0.5">{label}</p>
        <p className={`text-sm text-slate-800 ${mono ? 'font-mono' : 'font-medium'} truncate`}>
          {value}
        </p>
      </div>
    </div>
  );
}

export function SessionDetailSheet({
  journey,
  open,
  onOpenChange,
}: SessionDetailSheetProps) {
  const { openOrderInAdmin } = useShopifyAdmin();

  if (!journey) return null;

  const events = journey.events || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-4 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
              <User className="h-5 w-5 text-slate-600" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-base">
                Session Details
              </DialogTitle>
              <DialogDescription className="font-mono text-xs">
                {journey.id}
              </DialogDescription>
            </div>
            {journey.converted && (
              <Badge variant="success">
                Converted
              </Badge>
            )}
          </div>
        </DialogHeader>

        <div className="py-4 space-y-6 overflow-y-auto flex-1">
          {/* Session Info */}
          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              Session Information
            </h4>
            <div className="bg-slate-50 rounded-lg p-3 divide-y divide-slate-200/60">
              <InfoRow
                icon={Fingerprint}
                label="Client ID"
                value={journey.clientId}
                mono
              />
              {journey.customerId && (
                <InfoRow
                  icon={UserCircle}
                  label="Customer ID"
                  value={journey.customerId}
                  mono
                />
              )}
              {journey.distributorId && (
                <InfoRow
                  icon={Building}
                  label="Distributor ID"
                  value={journey.distributorId}
                  mono
                />
              )}
              <InfoRow
                icon={Calendar}
                label="Started"
                value={formatDateTime(journey.startedAt)}
              />
              <InfoRow
                icon={Hash}
                label="Total Events"
                value={`${events.length} events`}
              />
              {journey.orderId && (
                <InfoRow
                  icon={ShoppingCart}
                  label="Order ID"
                  value={
                    <button
                      onClick={() => openOrderInAdmin(journey.orderId!)}
                      className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-700 hover:underline cursor-pointer"
                    >
                      #{journey.orderId}
                      <ExternalLink className="h-3 w-3" />
                    </button>
                  }
                />
              )}
            </div>
          </div>

          {/* Attribution Info */}
          {journey.attribution && (
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                Attribution
              </h4>
              <div className="bg-slate-50 rounded-lg p-3 divide-y divide-slate-200/60">
                {journey.attribution.source && (
                  <InfoRow
                    icon={Globe}
                    label="Source"
                    value={journey.attribution.source}
                  />
                )}
                {journey.attribution.medium && (
                  <InfoRow
                    icon={Hash}
                    label="Medium"
                    value={journey.attribution.medium}
                  />
                )}
                {journey.attribution.campaign && (
                  <InfoRow
                    icon={Hash}
                    label="Campaign"
                    value={journey.attribution.campaign}
                  />
                )}
              </div>
            </div>
          )}

          {/* Event Timeline */}
          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
              Event Timeline
            </h4>
            <JourneyTimeline
              session={{
                id: journey.id,
                clientId: journey.clientId,
                events: events,
                startedAt: journey.startedAt,
                converted: journey.converted,
                convertedAt: journey.convertedAt,
                orderId: journey.orderId ? parseInt(journey.orderId) : undefined,
              }}
              showSummary={false}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
